
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthService } from '../services/authService';
import { logWithTimestamp } from '../utils/authUtils';

/**
 * Enhanced hook for verifying user session validity and handling session-related actions
 */
export const useSessionCheck = () => {
  const navigate = useNavigate();

  /**
   * Verifies that user session is valid
   * @param options Optional configuration for behavior on session checks
   * @returns True if session is valid, false otherwise
   */
  const verifyUserSession = async (options: {
    showToast?: boolean; 
    redirectToAuth?: boolean;
    redirectPath?: string;
  } = { showToast: true, redirectToAuth: true, redirectPath: '/auth' }): Promise<boolean> => {
    try {
      logWithTimestamp('Verifying user session');
      const { session, error } = await AuthService.getSession();
      
      if (error || !session) {
        logWithTimestamp('Session expired or not found');
        
        if (options.redirectToAuth) {
          logWithTimestamp(`Redirecting to ${options.redirectPath}`);
          navigate(options.redirectPath);
        }
        
        if (options.showToast) {
          toast.error('Your session has expired. Please sign in again.');
        }
        
        return false;
      }
      
      logWithTimestamp('Session valid');
      return true;
    } catch (error) {
      console.error('Error checking session:', error);
      
      if (options.showToast) {
        toast.error('Authentication error. Please sign in again.');
      }
      
      return false;
    }
  };

  /**
   * Checks session without side effects (no redirects or toasts)
   * @returns True if session is valid, false otherwise
   */
  const checkSessionStatus = async (): Promise<boolean> => {
    try {
      const { session } = await AuthService.getSession();
      return !!session;
    } catch (error) {
      console.error('Silent session check error:', error);
      return false;
    }
  };

  return {
    verifyUserSession,
    checkSessionStatus
  };
};
